import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { Combobox } from '@aeris/ui'

import { createBrand, fetchBrands } from '../lib/api/brands.ts'
import { ApiError } from '../lib/api.ts'

/**
 * A marca do produto, escolhida em vez de digitada — F158.
 *
 * ## O que este campo resolve
 *
 * `product.brand` era um `Input` de texto livre, e o resultado, medido: **2,9%
 * do catálogo com marca**, e a pergunta "quais marcas mais têm troca"
 * respondendo uma linha só. Texto livre não é liberdade — é adivinhação. Quem
 * cadastra não tem como saber se a loja escreve "Multilaser" ou "MULTILASER",
 * e o `group by` de qualquer relatório trata as duas como marcas diferentes.
 *
 * ## Criar de dentro do popup, e não noutra tela
 *
 * A marca nova aparece **no meio do cadastro de um produto**, com a caixa na
 * mão. Mandar a pessoa a Configurações › Marcas, criar a linha e voltar é o
 * caminho que faz alguém desistir e deixar o campo vazio — que é como se chega
 * a 2,9%. Aqui ela digita, vê "Criar a marca «Elgin»" na primeira posição, e
 * segue.
 *
 * ## Filtragem local
 *
 * `Combobox` nunca filtra (é regra dele), então quem filtra é este componente.
 * Local, e não por requisição a cada letra, porque a lista de marcas de uma
 * loja tem dezenas de linhas — buscar no servidor seria latência trocada por
 * nada. A lista de produtos, que tem cem mil, faz o contrário e está certa.
 */

/**
 * O valor da linha "criar".
 *
 * Um sentinela e não um `boolean` ao lado porque `Combobox` escolhe por
 * `value`: a linha de criar precisa ser uma opção como as outras para ganhar a
 * seta, o Enter e o realce. Começa com um caractere que nome de marca não tem.
 */
const CREATE = ' criar'

export type BrandFieldProps = {
  /** O nome da marca, como o produto a guarda. Vazio é "sem marca". */
  value: string
  onValueChange: (name: string) => void
  id?: string
  invalid?: boolean
  'aria-describedby'?: string | undefined
}

export const BrandField = ({
  value,
  onValueChange,
  id,
  invalid = false,
  ...aria
}: BrandFieldProps): ReactNode => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  const brands = useQuery({
    queryKey: ['brands', 'active'],
    queryFn: async ({ signal }) => await fetchBrands('active', signal),
  })

  const create = useMutation({
    mutationFn: async (name: string) => await createBrand({ name }),
    onSuccess: async (brand) => {
      setError(null)
      onValueChange(brand.name)
      await queryClient.invalidateQueries({ queryKey: ['brands'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível criar a marca.'
      )
    },
  })

  const term = search.trim().toLowerCase()
  const all = brands.data ?? []
  const matches = all.filter((brand) => brand.name.toLowerCase().includes(term))

  /*
   * A linha de criar aparece só quando a busca não é, exatamente, uma marca que
   * já existe. Sem essa comparação, digitar "Elgin" inteiro ofereceria criar uma
   * segunda "Elgin" — que o servidor recusaria, depois do clique.
   */
  const exact = all.some((brand) => brand.name.trim().toLowerCase() === term)
  const offerCreate = term.length > 0 && !exact

  const items = [
    ...(offerCreate
      ? [{ value: CREATE, label: `Criar a marca “${search.trim()}”` }]
      : []),
    ...matches.map((brand) => ({
      value: brand.name,
      label: brand.name,
      hint:
        brand.usageCount === 0
          ? undefined
          : `${String(brand.usageCount)} produto${brand.usageCount === 1 ? '' : 's'}`,
    })),
  ]

  return (
    <div className="flex flex-col gap-1">
      <Combobox
        // Espalhado, e não `id={id}`: `ComboboxProps.id` é `string` opcional, e
        // `exactOptionalPropertyTypes` recusa `undefined` explícito.
        {...(id === undefined ? {} : { id })}
        value={value === '' ? null : value}
        selectedLabel={value === '' ? null : value}
        onValueChange={(chosen) => {
          if (chosen === CREATE) {
            create.mutate(search.trim())
            return
          }
          onValueChange(chosen ?? '')
        }}
        items={items}
        search={search}
        onSearchChange={setSearch}
        loading={brands.isPending || create.isPending}
        invalid={invalid}
        clearable
        placeholder="Elgin"
        emptyMessage={
          brands.isError
            ? 'Não foi possível carregar as marcas.'
            : 'Nenhuma marca cadastrada ainda. Digite para criar a primeira.'
        }
        {...aria}
      />
      {error === null ? null : (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {error}
        </p>
      )}
    </div>
  )
}
